import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronsUpDown, Plus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  deriveLibraryTitle,
  getLibrarySourceTypeLabel,
  type LibrarySourceType,
} from "@/lib/library";
import { getSourceTypeIcon } from "@/lib/sourceTypeIcons";
import { getSourceRowToneVars } from "@/lib/sourceTypeRowTone";
import { detectSourceIdentifier, type DetectedSourceIdentifier } from "@/lib/sourceIdentifiers";

export type SourceComboboxOption = {
  id: string;
  title: string;
  subtitle: string | null;
  display_title: string | null;
  author_text: string | null;
  source_type: LibrarySourceType;
  container_title?: string | null;
};

type SourceComboboxProps = {
  value: string;
  options: SourceComboboxOption[];
  onChange: (id: string, option: SourceComboboxOption | null) => void;
  onCreateNew?: (initialQuery: string, identifier?: DetectedSourceIdentifier | null) => void;
  placeholder?: string;
  disabled?: boolean;
};

export const SourceCombobox = ({
  value,
  options,
  onChange,
  onCreateNew,
  placeholder = "Search My Library…",
  disabled,
}: SourceComboboxProps) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = useMemo(
    () => options.find((option) => option.id === value) ?? null,
    [options, value],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options.slice(0, 50);
    return options
      .filter((option) => {
        const haystack = [
          deriveLibraryTitle(option),
          option.author_text ?? "",
          option.container_title ?? "",
        ]
          .join(" ")
          .toLowerCase();
        return haystack.includes(q);
      })
      .slice(0, 50);
  }, [options, query]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 0);
    } else {
      setQuery("");
    }
  }, [open]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn("w-full justify-between font-normal", !selected && "text-muted-foreground")}
        >
          <span className="flex min-w-0 flex-1 items-center gap-2 truncate text-left">
            {selected ? (
              <>
                {(() => {
                  const Icon = getSourceTypeIcon(selected.source_type);
                  return (
                    <span className="inline-flex shrink-0 items-center gap-1 rounded-md border border-border bg-muted/40 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                      <Icon className="h-3 w-3 text-primary" />
                      {getLibrarySourceTypeLabel(selected.source_type)}
                    </span>
                  );
                })()}
                <span className="truncate">
                  {deriveLibraryTitle(selected)}
                  {selected.author_text ? ` — ${selected.author_text}` : ""}
                </span>
              </>
            ) : (
              <span>Choose a source from My Library…</span>
            )}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <div className="flex items-center border-b px-3">
          <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onPaste={(e) => {
              if (!onCreateNew) return;
              const pasted = e.clipboardData.getData("text");
              const detected = detectSourceIdentifier(pasted);
              if (detected) {
                e.preventDefault();
                setOpen(false);
                onCreateNew(pasted.trim(), detected);
              }
            }}
            placeholder={placeholder}
            className="h-10 border-0 px-0 shadow-none focus-visible:ring-0"
          />
        </div>
        <div className="max-h-72 overflow-y-auto py-1">
          {value && (
            <button
              type="button"
              onClick={() => {
                onChange("", null);
                setOpen(false);
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:bg-accent"
            >
              Clear selection
            </button>
          )}
          {filtered.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-muted-foreground">
              No matching sources in My Library.
            </div>
          ) : (
            filtered.map((option) => {
              const isSelected = option.id === value;
              const tone = getSourceRowToneVars(option.source_type);
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => {
                    onChange(option.id, option);
                    setOpen(false);
                  }}
                  style={{
                    backgroundColor: isSelected ? tone.selected : tone.idle,
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) e.currentTarget.style.backgroundColor = tone.hover;
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) e.currentTarget.style.backgroundColor = tone.idle;
                  }}
                  onFocus={(e) => {
                    if (!isSelected) e.currentTarget.style.backgroundColor = tone.hover;
                  }}
                  onBlur={(e) => {
                    if (!isSelected) e.currentTarget.style.backgroundColor = tone.idle;
                  }}
                  className={cn(
                    "flex w-full items-start gap-2 px-3 py-2 text-left text-sm transition-colors",
                  )}
                >
                  <Check
                    className={cn(
                      "mt-0.5 h-4 w-4 shrink-0",
                      isSelected ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      {(() => {
                        const Icon = getSourceTypeIcon(option.source_type);
                        return (
                          <span
                            className="inline-flex shrink-0 items-center gap-1 rounded-md border border-border bg-muted/40 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide"
                            style={{ color: tone.title }}
                          >
                            <Icon className="h-3 w-3" style={{ color: tone.title }} />
                            {getLibrarySourceTypeLabel(option.source_type)}
                          </span>
                        );
                      })()}
                      <span className="truncate font-medium" style={{ color: tone.title }}>
                        {deriveLibraryTitle(option)}
                      </span>
                    </div>
                    {option.author_text && (
                      <div className="mt-0.5 truncate text-xs" style={{ color: tone.meta }}>
                        {option.author_text}
                        {option.container_title ? ` · ${option.container_title}` : ""}
                      </div>
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>
        {onCreateNew && (
          <div className="border-t p-1">
            <button
              type="button"
              onClick={() => {
                const initial = query;
                setOpen(false);
                onCreateNew(initial);
              }}
              className="flex w-full items-center gap-2 rounded-sm px-3 py-2 text-sm hover:bg-accent"
            >
              <Plus className="h-4 w-4" />
              {query.trim() ? `Add "${query.trim()}" to My Library…` : "Add new source to My Library…"}
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
